/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useRef } from 'react'
import { BarChart } from '@tremor/react'
import { CircularProgress } from '@material-ui/core'

import moment, { Moment } from 'config/moment'
import { useApiCall } from 'hooks/useApi'
import { CustomCheckbox } from 'components/CustomCheckbox'

import { useLoadingTracker } from '../../hooks/useLoadingTracker'
import { formatDashboardNumber } from '../../utils/formatDashboardNumber'
import { formatCampaignFunnelMetric } from '../../utils/formatCampaignFunnelMetric'
import { getPushNotifications, PushNotificationsParams, PushNotificationsResponse } from './api'

const montserratStyle = { fontFamily: 'Montserrat, sans-serif', color: '#1E1E1E' }

const FUNNEL_COLUMNS = [
  'Audiencia potencial',
  'Audiencia impactada',
  'Walkthrough',
  'Beneficios canjeados',
] as const

const formatFunnelDisplay = (
  value: number | null | undefined,
  previousValue: number | null | undefined,
  options?: {
    isBaseline?: boolean
    naWhenZero?: boolean
  },
) => formatCampaignFunnelMetric(value, previousValue, options)

interface PushNotificationsProps {
  period: {
    month: number
    year: number
  }
  startPeriod1?: Moment
  endPeriod1?: Moment
  trigger: number
  /** Clears stale metrics as soon as Aplicar runs (even before this section fetches). */
  resetTrigger?: number
  // eslint-disable-next-line react/require-default-props
  onLoaded?: () => void
}

const PushNotifications: React.FC<PushNotificationsProps> = ({
  period,
  startPeriod1,
  endPeriod1,
  trigger,
  resetTrigger = 0,
  onLoaded,
}) => {
  const [pushData, setPushData] = useState<PushNotificationsResponse['data'] | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [onlyWithBenefits, setOnlyWithBenefits] = useState(false)
  const canFetchRef = useRef(false)
  const skipFilterFetchRef = useRef(true)
  const [pushCall] = useApiCall<PushNotificationsParams, PushNotificationsResponse>(
    getPushNotifications,
  )

  // Track loading state
  useLoadingTracker({ isLoading: loading, componentId: 'PushNotifications' })

  // Clear + loading only when Aplicar runs
  useEffect(() => {
    if (resetTrigger > 0) {
      setPushData(null)
      setError(null)
      setLoading(true)
    }
  }, [resetTrigger])

  const fetchPushData = async (callOnLoaded = false) => {
    try {
      setLoading(true)
      setPushData(null)
      setError(null)

      let startDate: string
      let endDate: string

      if (startPeriod1 && endPeriod1) {
        startDate = startPeriod1.format('YYYY-MM-DD')
        endDate = endPeriod1.format('YYYY-MM-DD')
      } else if (endPeriod1) {
        startDate = endPeriod1
          .clone()
          .startOf('month')
          .format('YYYY-MM-DD')
        endDate = endPeriod1.format('YYYY-MM-DD')
      } else {
        const periodMoment = moment()
          .year(period.year)
          .month(period.month)
        startDate = periodMoment
          .clone()
          .startOf('month')
          .format('YYYY-MM-DD')
        endDate = periodMoment
          .clone()
          .endOf('month')
          .format('YYYY-MM-DD')
      }

      const response = await pushCall({
        startDate,
        endDate,
        onlyWithBenefits,
      })

      if (response.data) {
        setPushData(response.data)
      }
    } catch (err) {
      setError('Error al cargar los datos de push notifications')
      console.error('Error fetching push notifications:', err)
    } finally {
      setLoading(false)
      if (callOnLoaded && onLoaded) {
        onLoaded()
      }
    }
  }

  useEffect(() => {
    if (trigger > 0) {
      canFetchRef.current = true
      fetchPushData(true)
    }
  }, [trigger])

  useEffect(() => {
    if (skipFilterFetchRef.current) {
      skipFilterFetchRef.current = false
      return
    }
    if (!canFetchRef.current) return
    fetchPushData(false)
  }, [onlyWithBenefits])

  const mainMetrics = [
    {
      title: 'Pushs de campañas',
      value: pushData?.campaignPushes || 0,
      funnel: pushData?.manualFunnel,
      campaignsCount: pushData?.manualCampaignsCount || 0,
    },
    {
      title: 'Pushs automatizadas',
      value: pushData?.automatedPushes || 0,
      funnel: pushData?.automatedFunnel,
      campaignsCount: pushData?.automatedCampaignsCount || 0,
    },
  ]

  const sent = pushData?.tableData?.sent || 0
  const received = pushData?.tableData?.received || 0
  const walkthrough = pushData?.tableData?.walkthrough || 0
  const conversions = pushData?.tableData?.conversions || 0

  const tableCombinedValues = [
    {
      key: 'sent',
      value: formatFunnelDisplay(sent, null, { isBaseline: true }),
    },
    {
      key: 'received',
      value: formatFunnelDisplay(received, sent),
    },
    {
      key: 'walkthrough',
      value: formatFunnelDisplay(walkthrough, received),
    },
    {
      key: 'conversions',
      value: formatFunnelDisplay(conversions, walkthrough, { naWhenZero: true }),
    },
  ]

  const renderHeader = () => (
    <div className="flex justify-between items-center mb-6">
      <div className="flex items-center gap-4">
        <span className="font-bold text-base block" style={montserratStyle}>
          Push notifications
        </span>
        <div className="flex items-center gap-2 text-sm" style={montserratStyle}>
          <CustomCheckbox
            checked={onlyWithBenefits}
            onChange={e => setOnlyWithBenefits(e.target.checked)}
            className="w-4 h-4 rounded border border-gray-400"
            disabled={loading}
            inputProps={{ 'aria-label': 'Solo push con beneficios asociados' }}
          />
          <span>Solo push con beneficios asociados</span>
        </div>
      </div>
      <button
        type="button"
        className="p-2 rounded border border-gray-300 hover:bg-gray-100 transition"
        aria-label="Descargar CSV"
        disabled={loading}
      >
        <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4"
          />
        </svg>
      </button>
    </div>
  )

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        {renderHeader()}
        <div className="text-red-500 text-center py-8" style={montserratStyle}>
          {error}
        </div>
      </div>
    )
  }

  if (loading || !pushData) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        {renderHeader()}
        <div className="flex items-center justify-center h-64">
          {loading ? (
            <CircularProgress size={40} />
          ) : (
            <div className="text-center text-gray-600 py-8" style={montserratStyle}>
              No hay datos disponibles para mostrar en este momento.
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      {renderHeader()}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="grid grid-cols-1 gap-4">
          {/* Métricas principales */}
          {mainMetrics.map(metric => (
            <div
              key={metric.title}
              className="bg-white rounded-xl shadow p-6 text-center"
              style={montserratStyle}
            >
              <span className="text-base font-bold mb-1 block">{metric.title}</span>
              <span className="text-3xl font-extrabold mb-4 block">
                {formatDashboardNumber(metric.campaignsCount)}
              </span>
              {/* Funnel — CTR no se renderiza */}
              {metric.funnel && (
                <div className="mt-4 space-y-2 text-left">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-700">Audiencia potencial</span>
                    <span className="font-semibold">
                      {formatFunnelDisplay(metric.funnel.sent, null, { isBaseline: true })}
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-700">Audiencia impactada</span>
                    <span className="font-semibold">
                      {formatFunnelDisplay(metric.funnel.received, metric.funnel.sent)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-700">Walkthrough</span>
                    <span className="font-semibold">
                      {formatFunnelDisplay(metric.funnel.walkthrough || 0, metric.funnel.received)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-700">Beneficios canjeados</span>
                    <span className="font-semibold">
                      {formatFunnelDisplay(
                        metric.funnel.conversions,
                        metric.funnel.walkthrough || 0,
                        { naWhenZero: true },
                      )}
                    </span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
        {/* Tabla de métricas */}
        <div className="mb-14 col-span-1 md:col-span-3">
          <div className="overflow-x-auto">
            <table
              className="min-w-full border-separate mb-4"
              style={{ borderSpacing: 0, ...montserratStyle }}
            >
              <thead>
                <tr>
                  {FUNNEL_COLUMNS.map(col => (
                    <th
                      key={col}
                      className="text-center text-xs text-gray-700 font-semibold bg-gray-100 px-4 py-2"
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  {tableCombinedValues.map(({ key, value }) => (
                    <td key={key} className="text-center px-4 py-2 text-sm">
                      {value}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
          {/* Gráfico de barras */}
          <div className="w-full h-[250px]">
            <BarChart
              data={pushData?.barData || []}
              index="name"
              categories={['value']}
              colors={['#26342E']}
              valueFormatter={v => (typeof v === 'number' ? formatDashboardNumber(v) : '')}
              yAxisWidth={60}
              showLegend={false}
              style={montserratStyle}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

PushNotifications.defaultProps = {
  startPeriod1: undefined,
  endPeriod1: undefined,
  resetTrigger: 0,
}

export default PushNotifications
